import { useState, useEffect, useRef, useCallback, type KeyboardEvent } from 'react';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { formatPersonName } from '@/lib/utils';


interface SearchResult {
  type: 'ticker' | 'insider' | 'politician';
  label: string;
  sub?: string;
  to: string;
}

const getTickerMatchScore = (
  query: string,
  ticker: string,
  companyName?: string | null,
) => {
  const q = query.toLowerCase();
  const tickerLower = ticker.toLowerCase();
  const companyLower = (companyName || '').toLowerCase();

  if (tickerLower === q) return 0;
  if (tickerLower.startsWith(q)) return 1;
  if (companyLower.startsWith(q)) return 2;
  if (tickerLower.includes(q)) return 3;
  if (companyLower.includes(q)) return 4;
  return 5;
};

const NavSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  // Run live search against the backend instead of relying on a capped preload,
  // which can miss valid symbols like TSM when they fall outside the first page.
  useEffect(() => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setResults([]);
      return;
    }

    let cancelled = false;
    const searchTerm = trimmedQuery.replace(/[,%]/g, ' ').trim();
    const pattern = `%${searchTerm}%`;

    const runSearch = async () => {
      const [profileRes, tradeTickerRes, insiderRes, politicianRes] = await Promise.all([
        supabase
          .from('company_profiles')
          .select('ticker, name')
          .or(`ticker.ilike.${pattern},name.ilike.${pattern}`)
          .limit(12),
        supabase
          .from('insider_trades')
          .select('ticker, company_name')
          .or(`ticker.ilike.${pattern},company_name.ilike.${pattern}`)
          .limit(24),
        supabase
          .from('insider_trades')
          .select('filer_name')
          .ilike('filer_name', pattern)
          .limit(12),
        supabase
          .from('congress_trades')
          .select('member_name')
          .ilike('member_name', pattern)
          .limit(12),
      ]);

      if (cancelled) return;

      const tickerMap = new Map<string, string | null>();

      (profileRes.data || []).forEach((row: any) => {
        if (!row?.ticker) return;
        tickerMap.set(row.ticker, row.name || tickerMap.get(row.ticker) || null);
      });

      (tradeTickerRes.data || []).forEach((row: any) => {
        if (!row?.ticker) return;
        if (!tickerMap.has(row.ticker) || !tickerMap.get(row.ticker)) {
          tickerMap.set(row.ticker, row.company_name || null);
        }
      });

      const tickerItems = Array.from(tickerMap.entries())
        .map(([ticker, company_name]) => ({ ticker, company_name }))
        .sort(
          (a, b) =>
            getTickerMatchScore(trimmedQuery, a.ticker, a.company_name) -
              getTickerMatchScore(trimmedQuery, b.ticker, b.company_name) ||
            a.ticker.localeCompare(b.ticker),
        )
        .slice(0, 5)
        .map((item) => ({
          type: 'ticker' as const,
          label: item.ticker,
          sub: item.company_name || undefined,
          to: `/stock/${item.ticker}`,
        }));

      const insiderSet = new Set<string>();
      (insiderRes.data || []).forEach((row: any) => {
        if (row?.filer_name) insiderSet.add(row.filer_name);
      });

      const insiderItems = Array.from(insiderSet)
        .slice(0, 5)
        .map((name) => ({
          type: 'insider' as const,
          label: formatPersonName(name),
          to: `/insider/${encodeURIComponent(name)}`,
        }));

      const politicianSet = new Set<string>();
      (politicianRes.data || []).forEach((row: any) => {
        if (row?.member_name) politicianSet.add(row.member_name);
      });

      const politicianItems = Array.from(politicianSet)
        .slice(0, 5)
        .map((name) => ({
          type: 'politician' as const,
          label: name,
          to: `/politician/${encodeURIComponent(name)}`,
        }));

      setResults([...tickerItems, ...insiderItems, ...politicianItems]);
    };

    const timeoutId = window.setTimeout(runSearch, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [query]);

  // Reset focus to first result whenever the result set changes.
  useEffect(() => {
    setFocusedIndex(0);
  }, [results]);

  // Close on outside click
  useEffect(() => {
    if (!open && !mobileExpanded) return;

    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setMobileExpanded(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, mobileExpanded]);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      navigate(result.to);
      setQuery('');
      setOpen(false);
      setMobileExpanded(false);
    },
    [navigate],
  );

  // Build the ordered list rendered in the dropdown so keyboard nav indexing
  // matches the visual order (tickers → insiders → politicians).
  const groupedResults = {
    tickers: results.filter((r) => r.type === 'ticker'),
    insiders: results.filter((r) => r.type === 'insider'),
    politicians: results.filter((r) => r.type === 'politician'),
  };
  const orderedResults: SearchResult[] = [
    ...groupedResults.tickers,
    ...groupedResults.insiders,
    ...groupedResults.politicians,
  ];

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setQuery('');
      setResults([]);
      setOpen(false);
      setMobileExpanded(false);
      return;
    }

    if (orderedResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setFocusedIndex((i) => (i + 1) % orderedResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setFocusedIndex((i) => (i - 1 + orderedResults.length) % orderedResults.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = orderedResults[focusedIndex] ?? orderedResults[0];
      if (target) handleSelect(target);
    }
  };

  // Build a flat-index lookup keyed by `${type}-${labelIndexInGroup}` so the
  // grouped render can mark the focused row without re-flattening.
  const focusedResult = orderedResults[focusedIndex];

  return (
    <div ref={ref} className="relative">
      {/* Desktop */}
      <div className="hidden lg:block">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search ticker, insider, or politician..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            className="h-8 w-64 rounded-lg border-0 bg-muted/30 pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/30"
          />
        </div>
      </div>

      {/* Mobile toggle */}
      <button className="p-2 text-muted-foreground hover:text-foreground lg:hidden" onClick={() => setMobileExpanded(!mobileExpanded)}>
        {mobileExpanded ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
      </button>

      {/* Mobile expanded input */}
      {mobileExpanded && (
        <div className="absolute right-0 top-full mt-1 w-[min(24rem,calc(100vw-2rem))] lg:hidden">
          <input
            type="text"
            placeholder="Search..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            autoFocus
            className="h-10 w-full rounded-lg border border-border/30 bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/30"
          />
        </div>
      )}

      {/* Dropdown */}
      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 max-h-80 overflow-y-auto rounded-lg border border-border/30 bg-card py-1 shadow-lg z-50 min-w-[280px]">
          {(['tickers', 'insiders', 'politicians'] as const).map((group) => {
            const items = groupedResults[group];
            if (items.length === 0) return null;
            return (
              <div key={group}>
                <div className="px-3 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {group === 'tickers' ? 'Tickers' : group === 'insiders' ? 'Insiders' : 'Politicians'}
                </div>
                {items.map((r, i) => {
                  const isFocused = focusedResult === r;
                  return (
                    <button
                      key={`${group}-${i}`}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors cursor-pointer ${
                        isFocused ? 'bg-muted/50' : 'hover:bg-muted/30'
                      }`}
                      onMouseEnter={() => setFocusedIndex(orderedResults.indexOf(r))}
                      onClick={() => handleSelect(r)}
                    >
                      <span className="font-medium text-foreground">{r.label}</span>
                      {r.sub && <span className="text-xs text-muted-foreground">{r.sub}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NavSearch;
